import { UTCDate } from "@date-fns/utc";
import { createContext } from "react";
import { removeAll, retrieve } from "../StorageContext/helpers";

export const AuthStorageKeys = {
  authData: "authData",
};

export type Credentials = {
  Jegernummer: string;
  Fodselsdato: string;
};

export type AuthData = {
  name: string;
  token: string;
  refreshToken: string;
  tokenExpiresAt: UTCDate;
  refreshTokenExpiresAt: UTCDate;
};

export type AuthErrorType =
  | "InvalidCredentials"
  | "MissingAuthData"
  | "TokenExpired"
  | "TokenRejected"
  | "Unknown";
export class AuthError extends Error {
  constructor(message: AuthErrorType) {
    super(message);
    this.name = "AuthError";
  }
}

abstract class AuthProvider {
  public authData(): AuthData | undefined {
    // StorageProvider kan ikke brukes her ettersom den er avhengig av å ha en aktiv AuthContext
    return retrieve<AuthData>(AuthStorageKeys.authData);
  }

  public token(): string {
    return this.authData()?.token ?? "";
  }

  public refreshTokenExpired(): boolean {
    const refreshTokenExpiresAt = this.authData()?.refreshTokenExpiresAt;
    return (
      refreshTokenExpiresAt === undefined || new UTCDate(refreshTokenExpiresAt) < new UTCDate()
    );
  }

  public currentUser(): string | undefined {
    return this.authData()?.name;
  }

  public async verify(): Promise<AuthData> {
    // Denne trenger strengt tatt ikke være async nå som localStorage brukes istedet for IDB (som er async).
    let authData = this.authData();
    if (!authData) {
      throw new AuthError("MissingAuthData");
    }
    const tokenExpiresAt = authData?.tokenExpiresAt;
    const tokenExpired =
      tokenExpiresAt === undefined || new UTCDate(tokenExpiresAt) < new UTCDate();

    if (tokenExpired && !this.refreshTokenExpired()) {
      return this.handleRefresh();
    }

    if (authData?.token && !tokenExpired) {
      return this.validateToken().catch((e) => {
        throw e;
      });
    } else if (tokenExpired) {
      this.handleLogout();
      throw new AuthError("TokenExpired");
    }
    throw new AuthError("Unknown");
  }

  abstract handleLogin(credentials: Credentials): Promise<AuthData>;
  abstract handleRefresh(): Promise<AuthData>;
  abstract validateToken(): Promise<AuthData>;

  async handleLogout(): Promise<void> {
    removeAll([AuthStorageKeys.authData]);
    const keys = await caches.keys();

    for (const key of keys) {
      if (!key.startsWith("settogskutt")) continue;

      try {
        const cache = await caches.open(key);
        const allCacheKeys = await cache.keys();
        await Promise.all(
          allCacheKeys
            .filter((req) => req.url.includes("/api/v1/"))
            .map((req) => cache.delete(req)),
        );
      } catch (_error) {}
    }
  }
}

const AuthContext = createContext({} as AuthProvider);

export { AuthContext as default, AuthProvider };
